"use client";

import { ReactNode, useEffect } from "react";
import { useAuth } from "../context/AuthContext";

/**
 * Central login gate for every routed page.
 *
 * Wrapping the router's `{children}` in the root layout means no UI page is
 * ever visible to an unauthenticated visitor: once MSAL has restored the
 * session (`isReady`) and no account is present, we trigger the login redirect
 * instead of rendering. Until then — and while unauthenticated — nothing is
 * rendered, so protected content never flashes and no token-less backend call
 * fires before auth is known.
 *
 * Individual pages may keep their own finer-grained checks (role/group gating,
 * deep-link handling), but they no longer need to remember the baseline
 * "redirect anonymous visitors to login" step — it happens here for all routes.
 */
export function AuthGuard({ children }: { children: ReactNode }) {
  const { isReady, isAuthenticated, login } = useAuth();

  useEffect(() => {
    if (isReady && !isAuthenticated) {
      login();
    }
  }, [isReady, isAuthenticated, login]);

  if (!isReady) return null;
  if (!isAuthenticated) return null;

  return <>{children}</>;
}
